import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, ShieldCheck, Camera, MessageCircle, Home as HomeIcon, Users, BedDouble, CalendarDays, Store, Factory, Utensils, Warehouse, Trees, Building2 } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PropertyCard from "@/components/locanest/PropertyCard";
import StatsBreakdownDialog from "@/components/locanest/StatsBreakdownDialog";
import LocationSearch from "@/components/locanest/LocationSearch";
import { CATEGORY_LIST } from "@/lib/categories";

const HERO = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2000&q=75";

const ICONS = { Home: HomeIcon, Users, BedDouble, CalendarDays, Store, Factory, Utensils, Warehouse, Trees };

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [category, setCategory] = useState("all");
  const [maxRent, setMaxRent] = useState("");
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ active_listings: 0, cities: 0, categories: 0, owners: 0 });
  const [statKind, setStatKind] = useState(null);
  const [categories, setCategories] = useState([]);
  const [locText, setLocText] = useState("");
  const [locPick, setLocPick] = useState(null);
  const [nearYou, setNearYou] = useState(null);

  useEffect(() => {
    api.get("/properties", { params: { limit: 8 } }).then(({ data }) => setFeatured(data)).catch(() => {});
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role !== "tenant" || !user.preferred_city) { setNearYou(null); return; }
    const params = { city: user.preferred_city, limit: 8 };
    if (user.preferred_locality) params.locality = user.preferred_locality;
    api.get("/properties", { params }).then(({ data }) => {
      if (data.length > 0) return setNearYou({ items: data, label: user.preferred_locality ? `${user.preferred_locality}, ${user.preferred_city}` : user.preferred_city, exact: true });
      return api.get("/properties", { params: { city: user.preferred_city, limit: 8 } }).then(({ data: d2 }) => setNearYou({ items: d2, label: user.preferred_city, exact: false }));
    }).catch(() => setNearYou(null));
  }, [user]);

  const goToLocation = (it) => {
    const params = new URLSearchParams();
    if (it.city) params.set("city", it.city);
    if (it.locality) params.set("locality", it.locality);
    if (category && category !== "all") params.set("category", category);
    if (maxRent) params.set("max_rent", maxRent);
    navigate(`/listings?${params.toString()}`);
  };

  const onSearch = (e) => {
    e.preventDefault();
    if (locPick && locPick.label === locText) return goToLocation(locPick);
    const params = new URLSearchParams();
    if (locText.trim()) params.set("q", locText.trim());
    if (category && category !== "all") params.set("category", category);
    if (maxRent) params.set("max_rent", maxRent);
    navigate(`/listings?${params.toString()}`);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden isolate">
        <div className="absolute inset-0 -z-10 bg-[#1A1F2C]">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1A1F2C]/80 via-[#1A1F2C]/60 to-[#C85A32]/45" />
        </div>

        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 pb-32 md:pt-28 md:pb-44">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/30 text-white text-xs uppercase tracking-[0.25em] backdrop-blur-md bg-white/5">
              <Building2 size={12} /> Rentals · Pan-India
            </div>
            <h1 className="mt-5 md:mt-6 font-display text-4xl sm:text-5xl md:text-7xl text-white leading-[1.05] md:leading-[1.02]" data-testid="hero-title">
              Find your next space.<br />
              <span className="text-[#D4DEC6]">Homes, PG, shops, warehouses & more.</span>
            </h1>
            <p className="mt-4 md:mt-6 text-white/85 text-base md:text-xl max-w-xl leading-relaxed">
              Verified owner listings across India. Real photos, direct listings, transparent process — for tenants and businesses alike.
            </p>
          </motion.div>

          {/* SEARCH BAR */}
          <motion.form
            onSubmit={onSearch}
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-10 bg-[#FDFBF7] border border-[#1A1F2C]/10 rounded-lg p-3 shadow-2xl grid grid-cols-1 md:grid-cols-12 gap-2"
            data-testid="hero-search-form"
          >
            <div className="md:col-span-4 flex items-center">
              <LocationSearch
                value={locText}
                onChange={(t) => { setLocText(t); if (locPick && locPick.label !== t) setLocPick(null); }}
                onPick={(it) => { setLocPick(it); goToLocation(it); }}
                onNear={({ lat, lng }) => navigate(`/listings?near=${lat},${lng}`)}
                placeholder="Area, locality, city or pincode"
                testId="hero-location"
              />
            </div>
            <div className="md:col-span-4 flex items-center gap-2 px-3 md:border-l border-[#1A1F2C]/10">
              <Building2 size={16} className="text-[#C85A32]" />
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="border-0 shadow-none focus:ring-0 px-0" data-testid="hero-category">
                  <SelectValue placeholder="Any category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any category</SelectItem>
                  {CATEGORY_LIST.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-center gap-2 px-3 md:border-l border-[#1A1F2C]/10">
              <Input type="number" placeholder="Max ₹" value={maxRent} onChange={(e) => setMaxRent(e.target.value)} className="border-0 shadow-none focus-visible:ring-0 px-0" data-testid="hero-max-rent" />
            </div>
            <Button type="submit" className="md:col-span-2 bg-[#C85A32] hover:bg-[#a8482a] text-white h-12 rounded-md" data-testid="hero-search-btn">
              <Search size={16} className="mr-2" /> Search
            </Button>
          </motion.form>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-white/85 text-sm">
            <span className="inline-flex items-center gap-2"><ShieldCheck size={16} className="text-[#D4DEC6]" /> Verified owners</span>
            <span className="inline-flex items-center gap-2"><Camera size={16} className="text-[#D4DEC6]" /> Real photos</span>
            <span className="inline-flex items-center gap-2"><MessageCircle size={16} className="text-[#D4DEC6]" /> Direct dealing</span>
          </div>
        </div>
      </section>

      {/* STATS — live from /api/stats, click any card for the real breakdown */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 -mt-20 md:-mt-24 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {[
            { k: stats.active_listings, v: "Active listings", kind: "listings" },
            { k: stats.cities, v: "Cities live", kind: "cities" },
            { k: stats.categories, v: "Property categories", kind: "categories" },
            { k: stats.owners, v: "Verified owners", kind: "owners" },
          ].map((s) => (
            <button
              type="button"
              key={s.v}
              onClick={() => setStatKind(s.kind)}
              className="text-left bg-[#FDFBF7] border border-[#1A1F2C]/10 p-5 md:p-6 rounded-lg hover:border-[#C85A32]/50 hover:shadow-md transition-all group"
              data-testid={`stat-${s.v.split(" ")[0].toLowerCase()}`}
            >
              <div className="font-display text-3xl md:text-4xl text-[#C85A32]">{s.k}</div>
              <div className="text-xs md:text-sm text-[#5C6B7A] mt-1 flex items-center justify-between">{s.v} <ArrowRight size={13} className="opacity-0 group-hover:opacity-100 transition-opacity" /></div>
            </button>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-[#5C6B7A] text-right" data-testid="stats-live-note">Live numbers from our database · tap a card to see the breakdown</div>
      </section>
      <StatsBreakdownDialog kind={statKind} onClose={() => setStatKind(null)} />

      {/* CATEGORY GRID */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-20">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Explore by category</div>
            <h2 className="font-display text-3xl md:text-5xl mt-2">Rent anything, anywhere.</h2>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3 md:gap-4" data-testid="category-grid">
          {CATEGORY_LIST.map((c) => {
            const Icon = ICONS[c.icon] || HomeIcon;
            const count = categories.find((x) => x.key === c.key)?.count || 0;
            return (
              <Link
                key={c.key}
                to={`/listings?category=${c.key}`}
                className="group bg-[#F0EBE1] border border-[#1A1F2C]/10 rounded-lg p-3.5 md:p-6 flex items-center gap-3 md:gap-4 hover:-translate-y-1 hover:shadow-lg hover:border-[#C85A32]/40 transition-all"
                data-testid={`category-${c.key}`}
              >
                <div className="w-10 h-10 md:w-14 md:h-14 shrink-0 rounded-md bg-[#FDFBF7] border border-[#1A1F2C]/10 grid place-items-center text-[#C85A32] group-hover:bg-[#C85A32] group-hover:text-white transition-colors">
                  <Icon size={22} />
                </div>
                <div>
                  <div className="font-display text-[15px] sm:text-lg md:text-xl leading-tight">{c.label}</div>
                  <div className="text-xs text-[#5C6B7A] mt-1">{count} listings</div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* NEAR YOU — tenants with a saved preferred location (from signup / profile) */}
      {nearYou && nearYou.items.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 mt-20" data-testid="near-you-section">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">For you</div>
              <h2 className="font-display text-3xl md:text-5xl mt-2">Rentals near you in {nearYou.label}</h2>
              {!nearYou.exact && <p className="text-sm text-[#5C6B7A] mt-1">Nothing in {user.preferred_locality} yet — showing the rest of {user.preferred_city}. <Link to="/profile?section=details" className="text-[#C85A32]">Change area</Link></p>}
            </div>
            <Link to={`/listings?city=${encodeURIComponent(user.preferred_city)}${nearYou.exact && user.preferred_locality ? `&locality=${encodeURIComponent(user.preferred_locality)}` : ""}`} className="hidden md:inline-flex items-center text-sm text-[#2B3A67] hover:text-[#C85A32]" data-testid="near-you-see-all">
              See all <ArrowRight size={14} className="ml-1" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {nearYou.items.map((p) => <PropertyCard key={p.id} p={p} />)}
          </div>
        </section>
      )}

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-20">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Newly listed</div>
            <h2 className="font-display text-3xl md:text-5xl mt-2">Recent additions</h2>
          </div>
          <Link to="/listings" className="hidden md:inline-flex items-center text-sm text-[#2B3A67] hover:text-[#C85A32]" data-testid="see-all-link">
            See all <ArrowRight size={14} className="ml-1" />
          </Link>
        </div>

        {featured.length === 0 ? (
          <div className="text-[#5C6B7A]">Loading properties…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p) => <PropertyCard key={p.id} p={p} />)}
          </div>
        )}
      </section>

      {/* OWNER CTA — Launch offer explicitly framed */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-24">
        <div className="bg-[#2B3A67] text-white rounded-lg overflow-hidden grid md:grid-cols-2">
          <div className="p-10 md:p-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C85A32]/20 text-[#D4DEC6] text-xs uppercase tracking-[0.25em]">
              Launch offer · For owners
            </div>
            <h3 className="font-display text-3xl md:text-5xl mt-4 leading-[1.05]">List your property free during launch.</h3>
            <p className="mt-4 text-white/80 max-w-md leading-relaxed">
              Home, shop, flat, factory or land — publish your listing on LocaNest at zero cost during our launch phase. Verification, photo standards and listing quality checks still apply. This is a limited-period launch offer.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild className="bg-[#C85A32] hover:bg-[#a8482a] rounded-md" data-testid="cta-list-property">
                <Link to="/register">Become an owner</Link>
              </Button>
              <Button asChild variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white rounded-md">
                <Link to="/listings">Browse listings</Link>
              </Button>
            </div>
          </div>
          <div className="hidden md:block relative">
            <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=75" alt="" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
